import { useCallback, useEffect, useState } from 'react';
import { Undo2, Split } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getClientPick, sendBackClientPick } from '@/services/deals.service.js';
import { forkQuoteWithoutPilot, setPilotMembership } from '@/services/quotes.service.js';
import QuoteWizardSheet from '@/components/sheets/QuoteWizardSheet.jsx';
import { formatCurrency } from '@/lib/format.js';
import { formatDateOrEmpty, formatDateTime } from '@/lib/date-display.js';

// THE CLIENT'S PICK (David 2026-09-23, McCallum / N169BT: "i need to see where
// the bind is and how to change things and be able to remedy these things").
//
// When the client selects a quote, TRACON saves a copy of it: that copy is what
// the binding page shows, what the payment is computed from, and what the CRM
// Deal mirrors. Editing the quote afterwards never reached it, and nothing said
// so. This card shows when that selection is out of date with the quote (the
// client's link already re-offers the current quote: pick_changed) and offers
// the two remedies David chose:
//
//   SEND BACK: void the old pick; the client's link shows the quotes again and
//   they choose. The client agrees to the new price themselves.
//   OFFER BOTH: when the change was a pilot added, fork the quote without that
//   pilot (born unpriced: the broker enters the carrier's number), send the old
//   pick back, and the client chooses between the two once both are presented.
//
// The server decides what differs and whether either is allowed; this renders it.
// Renders nothing until the client has picked.

const ymd = (s) => formatDateOrEmpty(s);
const when = (iso) => (iso ? formatDateTime(iso) : '');
const names = (list) => (list || []).map((p) => p.name || 'unnamed pilot').join(', ');

function changeLine(c) {
  if (c.field === 'premium') return `Premium ${formatCurrency(c.pick)} → ${formatCurrency(c.current)}`;
  if (c.field === 'effective_date') return `Effective ${ymd(c.pick)} → ${ymd(c.current)}`;
  if (c.field === 'pilots') {
    const parts = [];
    if (c.added?.length) parts.push(`${names(c.added)} added`);
    if (c.removed?.length) parts.push(`${names(c.removed)} removed`);
    return `Pilots: ${parts.join('; ')}`;
  }
  return null;
}

export default function ClientPickCard({ dealId, refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [dialog, setDialog] = useState(null); // 'send_back' | 'offer_both'
  const [busy, setBusy] = useState(false);
  const [altQuoteId, setAltQuoteId] = useState(null);

  const load = useCallback(() => {
    if (!dealId) return Promise.resolve(null);
    return getClientPick(dealId)
      .then((d) => { setData(d); setFailed(false); return d; })
      .catch(() => { setFailed(true); return null; });
  }, [dealId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const changed = () => { if (typeof onChanged === 'function') onChanged(); };

  // A confirmation is a read, not an echo: report what the server now holds.
  const sendBack = async (pickId) => {
    await sendBackClientPick(dealId, pickId);
    const after = await load();
    if (!after) return 'The old pick was sent back, but the result could not be re-read. Reload the deal to check.';
    return after.sent_back ? null : 'The send-back ran, but the deal does not read as sent back. Reload the deal to check.';
  };

  const doSendBack = async () => {
    setBusy(true);
    try {
      const warn = await sendBack(data.pick.id);
      if (warn) toast.info(warn);
      else toast.success('Sent back. The client will see the quotes and choose again. Use Present if you want to let them know.');
      setDialog(null);
      changed();
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Could not send the pick back');
    } finally { setBusy(false); }
  };

  const doOfferBoth = async () => {
    const added = (data?.changes || []).find((c) => c.field === 'pilots')?.added || [];
    if (!added.length || !data?.current?.quote_id) return;
    setBusy(true);
    let altId = null;
    try {
      // Fork FIRST: if it fails, nothing has changed and the client's pick stands.
      const res = await forkQuoteWithoutPilot(data.current.quote_id, { contact_id: added[0].contact_id, name: added[0].name || '' });
      altId = res?.quote_id || null;
      (res?.warnings || []).forEach((w) => toast.info(w));
      if (!altId) throw new Error('The alternate was not created.');
      for (const p of added.slice(1)) {
        await setPilotMembership(altId, { contact_id: p.contact_id, name: p.name || '', on_quote: false });
      }
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Could not create the alternate. The client\'s pick is unchanged.');
      setBusy(false);
      return;
    }
    try {
      const warn = await sendBack(data.pick.id);
      if (warn) toast.info(warn);
      toast.success(`Alternate created without ${names(added)}. Enter the carrier's price, then Present both.`);
    } catch (err) {
      toast.error(`Alternate created, but the old pick was not sent back: ${err?.body?.error || err?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
      setDialog(null);
      setAltQuoteId(altId);
      changed();
    }
  };

  const wizard = (
    <QuoteWizardSheet
      dealId={dealId}
      quoteId={altQuoteId}
      open={!!altQuoteId}
      onOpenChange={(o) => { if (!o) setAltQuoteId(null); }}
      onSaved={() => { load(); changed(); }}
    />
  );

  if (failed) {
    return (
      <div className="rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
        Could not read the client&apos;s pick for this deal. This is a failed read, not an empty one.
      </div>
    );
  }

  // Sent back and not yet re-picked.
  if (!data?.pick && data?.sent_back) {
    const sb = data.sent_back;
    return (
      <div className="rounded-card border border-border bg-surface px-3 py-2 text-xs">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">Sent back to client</span>
        <span className="ml-2 text-text">{when(sb.at)}. Waiting for them to choose again.</span>
        {sb.premium != null ? <span className="ml-2 text-text-dim">Old pick: <span className="line-through">{formatCurrency(sb.premium)}</span></span> : null}
        {wizard}
      </div>
    );
  }
  if (!data?.pick) return wizard;

  const { pick, current, changes = [], pilots = {}, refresh = {} } = data;
  const stale = changes.length > 0;
  const status = pick.paid ? 'paid' : pick.signed_at ? 'signed, not paid' : 'not signed, not paid';
  const pilotsChange = changes.find((c) => c.field === 'pilots') || null;
  // Offering both only makes sense when the RISK changed by a pilot joining: the
  // old terms are then a real offer for the smaller roster. A price change alone
  // is not an alternate, and a pilot who left cannot be put back by a fork.
  const canOfferBoth = refresh.allowed && pilotsChange?.added?.length > 0 && !(pilotsChange?.removed?.length);
  const oldLine = [
    pick.carrier || 'Unknown carrier',
    pick.premium != null ? formatCurrency(pick.premium) : 'no premium',
    pick.effective_date ? `eff ${ymd(pick.effective_date)}` : null,
    `${pilots.pick?.length || 0} pilots`,
  ].filter(Boolean).join(' · ');
  const newLine = current ? [
    current.carrier || pick.carrier || 'Unknown carrier',
    current.premium != null ? formatCurrency(current.premium) : 'no premium',
    current.effective_date ? `eff ${ymd(current.effective_date)}` : null,
    Array.isArray(pilots.current) ? `${pilots.current.length} pilots` : null,
  ].filter(Boolean).join(' · ') : '';

  if (!stale) {
    return (
      <div className="rounded-card border border-border bg-surface px-3 py-2 text-xs">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">Client picked</span>
        <span className="ml-2 font-semibold text-text">{oldLine}</span>
        <span className="ml-2 text-text-dim">{when(pick.created_at)}, {status}</span>
        {!pick.paid && current ? <span className="ml-2 text-success">Matches the current quote</span> : null}
        {data.pilots_unknown && !pick.paid ? <span className="ml-2 text-warning">(pilots not compared: roster unreadable)</span> : null}
        {wizard}
      </div>
    );
  }

  return (
    <div className="rounded-card border border-warning/50 bg-warning/10 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div>
          <p className="text-sm font-semibold text-warning">The client&apos;s selection is out of date</p>
          <p className="text-[11px] text-text-muted">
            They picked before the quote changed. Their link already shows the current quote and asks them to choose
            again; the old selection is still on the deal until they do, or until you clear it.
          </p>
        </div>
        {refresh.allowed ? (
          <div className="ml-auto flex flex-wrap gap-1.5">
            <Button type="button" size="sm" className="h-7 gap-1 text-xs" onClick={() => setDialog('send_back')}>
              <Undo2 size={13} /> Send back to client
            </Button>
            {canOfferBoth ? (
              <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => setDialog('offer_both')}>
                <Split size={13} /> Offer both
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-text-dim">Selected {when(pick.created_at)}</span>
        <span className="text-text-muted line-through">{oldLine}</span>
        <span className="text-text-dim">Quote now</span>
        <span className="font-semibold text-text">{newLine}</span>
      </div>
      <ul className="mt-1.5 space-y-0.5 text-[11px] text-text-muted">
        {changes.map((c) => <li key={c.field}>{changeLine(c)}</li>)}
      </ul>
      {!refresh.allowed && refresh.reason ? (
        <p className="mt-1 text-[11px] text-text-dim">{refresh.reason}</p>
      ) : null}

      <Dialog open={dialog === 'send_back'} onOpenChange={(o) => { if (!busy && !o) setDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send back to the client?</DialogTitle>
            <DialogDescription>
              Clears the old selection from the deal and moves it back to Presented. The client chooses again from the
              current quote on their link. Nothing is emailed or texted; use Present afterwards if you want to tell them.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <span className="text-text-dim">Clear</span>
            <span className="text-text-muted line-through">{oldLine}</span>
            <span className="text-text-dim">They choose from</span>
            <span className="font-semibold text-text">{newLine}</span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="button" disabled={busy} onClick={doSendBack}>{busy ? 'Sending back...' : 'Send back'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'offer_both'} onOpenChange={(o) => { if (!busy && !o) setDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Offer both?</DialogTitle>
            <DialogDescription>
              Keeps the current quote and adds an alternate without {names(pilotsChange?.added)}. The alternate starts with
              no price: you enter the carrier&apos;s number for it next. The old selection is cleared so the client chooses
              between the two once you Present them.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <span className="text-text-dim">Option 1</span>
            <span className="font-semibold text-text">{newLine} (with {names(pilotsChange?.added)})</span>
            <span className="text-text-dim">Option 2</span>
            <span className="text-text">
              {current?.carrier || pick.carrier || 'Carrier'} without {names(pilotsChange?.added)}, price to enter
              {pick.premium != null ? ` (it was ${formatCurrency(pick.premium)} before)` : ''}
            </span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="button" disabled={busy} onClick={doOfferBoth}>{busy ? 'Working...' : 'Create alternate and send back'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {wizard}
    </div>
  );
}
